import type { ComponentProps } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Card,
  CardHeader,
  makeStyles,
  Link as FluentLink,
  mergeClasses,
  tokens,
  Caption1,
  Caption1Strong,
  Tooltip,
} from '@fluentui/react-components';
import { Edit12Filled, OpenRegular } from '@fluentui/react-icons';
import { AccessType } from '@modules/access/types';
import type { TItemCardStatus, TProductSubmissionInfo } from '@modules/items/types/folders.types';
import type { TAction, TItem } from '@modules/items/types/items.types';
import getAvatarInitials from '@shared/helpers/getAvatarInitials';
import { AgoDateLabel, DateLabel } from './dates';
import { ControlledTooltip } from './ControlledTooltip';
import { ItemActionsMenu } from './ItemActionsMenu';
import { SQUARE_AVATAR_LARGE_FONT_MAX_CHARACTERS, SQUARE_CARD_HEIGHT } from './Square.constants';
import { useSquareCardStyles } from './Square.styles';
import ItemCardStatusBadges from './ItemCardStatusBadges';

type TAvatarColor = NonNullable<ComponentProps<typeof Avatar>['color']>;
type TAvatarIcon = ComponentProps<typeof Avatar>['icon'];

type TProps = {
  item: TItem;
  isRoot: boolean;
  actions: TAction[];
  productSubmissionInfos?: TProductSubmissionInfo[]; // only for products (i.e. isRoot === true)
  cardStatus?: TItemCardStatus; // only for Product/Jurisdiction/QM navigation cards
  avatarIcon?: TAvatarIcon;
  avatarCharacterCount?: number;
  avatarColor?: TAvatarColor;
  avatarIconBrand?: boolean;
  href?: string;
  className?: string;
};

const useStyles = makeStyles({
  card: {
    height: SQUARE_CARD_HEIGHT,
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
  },
  avatar: {
    fontSize: '1.05rem',
  },
  avatarLongInitials: {
    fontSize: tokens.fontSizeBase200,
  },
  avatarBrandIcon: {
    '& .fui-Avatar__icon': {
      color: tokens.colorBrandForeground1,
    },
  },
  details: {
    position: 'absolute',
    right: tokens.spacingHorizontalL,
    bottom: tokens.spacingVerticalL,
    left: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
});

const Square = ({
  item,
  isRoot: isProduct,
  actions,
  productSubmissionInfos,
  cardStatus,
  avatarIcon,
  avatarCharacterCount,
  avatarColor = 'colorful',
  avatarIconBrand = false,
  href: hrefProp,
  className,
}: TProps) => {
  const navigate = useNavigate();
  const href = hrefProp ?? `/${isProduct ? 'product' : 'folder'}/${item._id}`;

  const availableActions: TAction[] = [
    {
      children: 'Open',
      icon: <OpenRegular />,
      order: 0,
      enable: () => true,
      show: () => true,
      onClick: () => navigate(href),
    },
    ...actions,
  ];

  const nextSubmission = productSubmissionInfos?.find((info) => info.product === item._id);

  const avatarInitials = avatarIcon ? undefined : getAvatarInitials(item.name, avatarCharacterCount);

  const classes = useStyles();
  const squareClasses = useSquareCardStyles();

  return (
    <Link to={href} className="h-fit w-fit">
      <Card className={mergeClasses(squareClasses.card, squareClasses.revealActions, classes.card, className)}>
        <CardHeader
          image={
            <Avatar
              initials={avatarInitials}
              icon={avatarIcon}
              size={40}
              shape={isProduct ? 'square' : 'circular'}
              aria-label="square avatar"
              color={avatarColor}
              idForColor={avatarColor === 'colorful' ? item._id : undefined}
              className={mergeClasses(
                classes.avatar,
                avatarCharacterCount !== undefined &&
                  Array.from(avatarInitials ?? '').length > SQUARE_AVATAR_LARGE_FONT_MAX_CHARACTERS &&
                  classes.avatarLongInitials,
                avatarIconBrand && classes.avatarBrandIcon
              )}
            />
          }
          header={
            <ControlledTooltip
              enable={item.name.length > 30 /* upper case letters take up more space */}
              relationship="label"
              content={item.name}
              withArrow
            >
              <FluentLink appearance="subtle">
                <span className={squareClasses.title}>{item.name}</span>
              </FluentLink>
            </ControlledTooltip>
          }
          action={
            item.accessRight < AccessType.EDIT ? undefined : (
              <ItemActionsMenu item={item} actions={availableActions} variant="card" />
            )
          }
        />
        <div className={classes.details}>
          <ItemCardStatusBadges cardStatus={cardStatus} />
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2 min-w-0 w-full">
            <div className="min-w-0">
              {productSubmissionInfos ? (
                nextSubmission ? (
                  <Caption1 className="max-w-full truncate">
                    Submission due:{' '}
                    <Caption1Strong>
                      <DateLabel short>{nextSubmission.dueDate}</DateLabel>
                    </Caption1Strong>
                  </Caption1>
                ) : (
                  <Caption1>No submission due</Caption1>
                )
              ) : null}
            </div>
            <Tooltip relationship="description" content="Last edited">
              <div className="flex gap-1 items-center shrink-0 whitespace-nowrap self-end">
                <Edit12Filled />
                <Caption1>
                  <AgoDateLabel short>{item.stats.modifiedDate}</AgoDateLabel>
                </Caption1>
              </div>
            </Tooltip>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export { Square };
